import { useEffect, useRef } from "react";
import { useCinematicMoments } from "@/hooks/useCinematicMoments";
import { CinematicMomentCard } from "./CinematicMomentCard";
import { useMobileMatchMode } from "@/lib/launchHardening/mobileMatchMode";
import { acquireSlot, releaseSlot } from "@/lib/launchHardening/priorityVisualQueue";
import { useDelayedBySignal } from "@/lib/sync/offsetOrchestration";
import { useEdgeSafeFlags } from "@/lib/resilience/edgeSafeFlags";
import { FOUNDATION_CINEMATIC_MOMENTS } from "@/lib/demo/executiveDemoMock";
import type { CinematicMoment } from "@/lib/cinematic/cinematicMomentsEngine";

/**
 * Inline stream — renders up to N active cinematic moments, gated by the
 * priority visual queue and the current mobile match-mode profile.
 */
export function CinematicMomentsStream({ max = 2 }: { max?: number }) {
  const mmm = useMobileMatchMode();
  const flags = useEdgeSafeFlags();
  const { moments, dismiss } = useCinematicMoments();
  // Phase 1 — cinematic / viral cards travel on the viral_card weight
  // (light delay; viral pacing > spoiler safety).
  const delayedMoments = useDelayedBySignal(moments, "viral_card");
  const baseCap = mmm.profile === "low" ? 1 : Math.min(max, mmm.overlayCap);
  // Density throttling — collapses cap when edge-safe / kill-switches degrade.
  const cap = Math.max(0, Math.floor(baseCap * (flags.allowCinematic ? flags.density : 0)));

  const slots = useRef<Map<string, string | null>>(new Map());
  const visible = delayedMoments.slice(0, cap);

  // Acquire / release slots so other heavy overlays back off when cinematic
  // moments occupy the screen.
  useEffect(() => {
    const visibleIds = new Set(visible.map((m) => m.id));
    for (const m of visible) {
      if (!slots.current.has(m.id)) {
        const slotId = acquireSlot("cinematic_moment", m.intensity >= 0.75 ? "high" : "normal", m.id);
        slots.current.set(m.id, slotId);
      }
    }
    for (const [id, slotId] of slots.current) {
      if (!visibleIds.has(id)) {
        releaseSlot(slotId);
        slots.current.delete(id);
      }
    }
    return () => {
      for (const slotId of slots.current.values()) releaseSlot(slotId);
      slots.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible.map((m) => m.id).join("|")]);

  // Filter out any moment that the queue rejected (slot === null) — keeps
  // the cap honest even when multiple events fire simultaneously.
  const renderable = visible.filter((m) => slots.current.get(m.id) !== null);
  if (!flags.allowCinematic) return null;

  // WORLD-CUP-LAUNCH-P2 — foundational fallback: never show an empty stream.
  if (renderable.length === 0) {
    const foundation: CinematicMoment[] = FOUNDATION_CINEMATIC_MOMENTS.slice(0, Math.max(1, cap || 2)).map((f) => ({
      ...f,
      createdAt: Date.now(),
    }));
    return (
      <div className="space-y-2">
        {foundation.map((m) => (
          <CinematicMomentCard key={m.id} moment={m} />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {renderable.map((m) => (
        <CinematicMomentCard key={m.id} moment={m} onDismiss={() => dismiss(m.id)} />
      ))}
    </div>
  );
}